/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { RefObject, useEffect } from 'react';

import { compiledHtmlConvert } from '../../utils/htmlToText';

/**
 * A custom React hook that intercepts the copy event to ensure hidden content
 * inside custom <action> elements is copied to the clipboard. The hook clones
 * the selected content, forces any <action> elements (without the "open" class)
 * to display their hidden content, and updates the clipboard with the modified HTML.
 *
 * This ensures that the hidden content is included in the clipboard data without
 * affecting the actual UI, as the original DOM remains unchanged during the copy event.
 *
 * Usage:
 * Call this hook within any functional component where you want to apply this behavior.
 *
 * Example:
 *
 * const Editor = () => {
 *   useCopyableActionContent();
 *   return <div> ... </div>;
 * };
 *
 * Important:
 * - Ensure that <action> elements have an "open" class to indicate visible content,
 *   and that hidden <action> elements are still in the DOM (e.g., set with display: none).
 * - This hook modifies the clipboard data for "text/html"
 * - "text/plain" data is also added to the clipboard with the plain text content, WITHOUT actions open
 *
 * @returns void
 */
const useCopyableActionContent = (ref: RefObject<HTMLElement>): void => {
  useEffect(() => {
    const targetElement = ref.current; // Store ref.current in a local variable
    if (!targetElement) return;
    if (targetElement.oncopy) {
      console.warn("useCopyableActionContent: Element already has a copy event listener - shouldn't happen?");
      return; // Already attached, exit early
    }

    const handleCopy = (event: ClipboardEvent) => {
      const selection = window.getSelection();

      if (!selection || selection.rangeCount === 0) {
        return; // No selection, exit early
      }
      if (!event.clipboardData) {
        return; // No clipboard data, exit early
      }

      /* First clone the selected content and modify it for plain text */
      const range = selection.getRangeAt(0);
      const plainTextDiv = document.createElement('div');
      plainTextDiv.appendChild(range.cloneContents());
      // Remove <action> elements without the "open" class for plain text
      const closedActions = plainTextDiv.querySelectorAll('action:not(.open)');
      closedActions.forEach((el) => el.remove());
      const plainTextContent = compiledHtmlConvert(plainTextDiv.innerHTML);

      /* Next clone the selected content a second time, and modify it for HTML */
      const htmlDiv = document.createElement('div');
      htmlDiv.appendChild(range.cloneContents());

      // Find all <action> elements in the cloned content and force them open
      const clonedActions = htmlDiv.querySelectorAll('action');
      clonedActions.forEach((action) => {
        action.classList.add('open'); // Force them to be open in the clone
      });
      const htmlContent = htmlDiv.innerHTML;

      // Set the modified HTML content in the clipboard
      if (event.clipboardData) {
        event.clipboardData.setData('text/html', htmlContent);
        event.clipboardData.setData('text/plain', plainTextContent); // Add plain text
      }

      event.preventDefault(); // Prevent default copy behavior
    };

    // Attach our custom copy listener to the specific element
    targetElement.addEventListener('copy', handleCopy);

    // Clean up: Remove our copy listener from the element
    return () => {
      targetElement.removeEventListener('copy', handleCopy);
    };
  }, [ref]);
};

export default useCopyableActionContent;
